import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Plus, Send, ShieldCheck } from 'lucide-react';
import Toast from '../../components/Toast';
import AddProspectModal from '../../components/prospection/AddProspectModal.jsx';
import Nr2jOrgCombobox from '../../components/nr2j/Nr2jOrgCombobox.jsx';
import Nr2jQuoteLivePreviewPanel from '../../components/nr2j/Nr2jQuoteLivePreviewPanel.jsx';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_QUOTE_EDITOR_GATE_KEYS,
  NR2J_QUOTE_ORG_GATE_KEYS,
  NR2J_QUOTE_SEND_GATE_LABELS,
  NR2J_VAT_OPTIONS,
  buildOrgFicheUrlFromQuoteGate,
  centsToEuros,
  computeQuoteLineTotals,
  defaultValidUntilInput,
  eurosToCents,
  formatCentsAsEuros,
  contactDisplayName,
  nr2jBillingPeriodSuffix,
  nr2jDiscountScopeLabel,
} from '../../utils/nr2jConstants';
import { createNr2jProspectFromModal } from '../../utils/nr2jCreateProspect.js';
import Nr2jContinueModal from '../../components/nr2j/Nr2jContinueModal.jsx';
import { useNr2jContinueAction } from '../../hooks/useNr2jContinueAction.js';
import {
  NR2J_CONTINUE_INTENTS,
  preflightQuoteSend,
  requirementsFromError,
} from '../../utils/nr2jContinuePreflight.js';
import {
  applyPlatformAccessToSession,
  approvalErrorMessage,
  approvalUserName,
  isApprovalRequiredError,
  sessionNeedsQuoteApproval,
} from '../../utils/nr2jApprovalUi.js';
import { notifyNr2jDataChanged } from '../../utils/nr2jRealtime.js';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

/**
 * Éditeur local : aucun POST tant que Enregistrer / Envoyer / Aperçu (persist).
 * Combobox société async — brouillon local préservé via modal de création (pas de navigation).
 */
export default function Nr2jQuoteEditor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetOrgId = searchParams.get('organizationId') || '';
  const { toasts, toast, removeToast } = useToast();
  const {
    continueReq,
    openContinue,
    closeContinue,
    runContinueRetry,
    withActionLock,
  } = useNr2jContinueAction();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [organizationId, setOrganizationId] = useState(presetOrgId);
  const [org, setOrg] = useState(null);
  const [contactId, setContactId] = useState('');
  const [validUntil, setValidUntil] = useState(() => defaultValidUntilInput());
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [lines, setLines] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [persistentMissing, setPersistentMissing] = useState([]);
  const [sendGateOpen, setSendGateOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createPrefill, setCreatePrefill] = useState('');
  const [accessReady, setAccessReady] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [sessionUser, setSessionUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('admin_user') || 'null');
    } catch {
      return null;
    }
  });

  const maxDiscountBps = useMemo(
    () => lines.reduce((max, line) => {
      const p = lineToPayload(line, 0);
      return Math.max(max, Number(p.discountPercentBps) || 0);
    }, 0),
    [lines],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const access = await apiService.getMyPlatformAccess();
        if (cancelled) return;
        const next = applyPlatformAccessToSession(access);
        setSessionUser(next);
      } catch {
        /* keep session snapshot */
      } finally {
        if (!cancelled) setAccessReady(true);
      }
    })();
    const onAuth = (event) => {
      setSessionUser(event?.detail || (() => {
        try {
          return JSON.parse(localStorage.getItem('admin_user') || 'null');
        } catch {
          return null;
        }
      })());
      setAccessReady(true);
    };
    window.addEventListener('authUserUpdated', onAuth);
    return () => {
      cancelled = true;
      window.removeEventListener('authUserUpdated', onAuth);
    };
  }, []);

  useEffect(() => {
    if (!accessReady) return;
    setNeedsApproval(sessionNeedsQuoteApproval(sessionUser, { maxDiscountBps }));
  }, [sessionUser, maxDiscountBps, accessReady]);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const catalogRes = await apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] }));
      setCatalog(catalogRes?.items || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (!organizationId) {
      setOrg(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiService.getNr2jOrganization(organizationId);
        if (!cancelled) setOrg(detail?.organization || detail);
      } catch {
        if (!cancelled) setOrg(null);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  function selectOrganization(nextOrg) {
    const id = nextOrg?.id || '';
    setOrganizationId(id);
    setOrg(nextOrg || null);
    setContactId('');
    setFieldErrors((prev) => ({ ...prev, contactId: false }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('organizationId', id);
      else next.delete('organizationId');
      return next;
    }, { replace: true });
  }

  function buildPayload() {
    return {
      organizationId,
      contactId: contactId || null,
      validUntil: validUntil ? new Date(validUntil).toISOString() : null,
      clientNote: clientNote || null,
      internalNote: internalNote || null,
      lines: lines.map(lineToPayload),
    };
  }

  function applyGateMissing(missing) {
    const list = Array.isArray(missing) ? missing : [];
    setPersistentMissing(list);
    const next = {};
    if (list.includes('validUntil') || !validUntil) next.validUntil = true;
    if (list.includes('recipientEmail')) next.contactId = true;
    setFieldErrors(next);
  }

  async function persistQuote() {
    if (!organizationId) {
      toast.error('Sélectionnez une société');
      throw new Error('ORG_REQUIRED');
    }
    const res = await apiService.createNr2jQuote(buildPayload());
    return res?.quote || res;
  }

  async function saveDraft() {
    setBusy(true);
    try {
      const quote = await persistQuote();
      toast.success(`Brouillon créé — ${quote.number}`);
      navigate(`/nr2j/devis/${quote.id}`, { replace: true });
    } catch (error) {
      if (error?.message !== 'ORG_REQUIRED') {
        toast.error(error?.message || 'Enregistrement impossible');
      }
    } finally {
      setBusy(false);
    }
  }

  async function previewPdf() {
    setBusy(true);
    try {
      const quote = await persistQuote();
      const version = quote.currentVersion;
      if (!version?.id) {
        throw new Error('Version devis indisponible pour l’aperçu PDF');
      }
      await apiService.downloadNr2jQuotePdf(quote.id, version.id, {
        number: quote.number,
        version: version.version,
        mode: 'preview',
      });
      toast.success(`Brouillon créé — ${quote.number}`);
      navigate(`/nr2j/devis/${quote.id}`, { replace: true });
    } catch (error) {
      if (error?.message !== 'ORG_REQUIRED') {
        toast.error(error?.message || 'Aperçu PDF impossible');
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendQuoteCore() {
    const quote = await persistQuote();
    try {
      await apiService.sendNr2jQuote(quote.id);
      notifyNr2jDataChanged({ entityType: 'QUOTE', action: 'SENT' });
      toast.success('Devis envoyé au client');
      navigate(`/nr2j/devis/${quote.id}`, { replace: true });
    } catch (error) {
      error.__nr2jQuote = quote;
      throw error;
    }
    return quote;
  }

  function resumeSendQuoteFromContinuePayload(payload, ctx = {}) {
    const validUntilResume = payload?.validUntil
      ? new Date(payload.validUntil).toISOString().slice(0, 10)
      : ctx.validUntil;
    return sendQuote({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      validUntil: validUntilResume,
      contactId: payload?.quoteContactId ?? ctx.contactId,
      skipPreflight: true,
    });
  }

  async function sendQuote(options = {}) {
    const run = async () => {
      setBusy(true);
      try {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const validUntilCtx = options.validUntil !== undefined ? options.validUntil : validUntil;
        const contactIdCtx = options.contactId !== undefined ? options.contactId : contactId;

        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        }
        if (options.validUntil !== undefined) setValidUntil(validUntilCtx);
        if (options.contactId !== undefined) setContactId(contactIdCtx);

        if (!options.skipPreflight) {
          const pre = preflightQuoteSend({
            org: orgCtx,
            contacts: contactsCtx,
            validUntil: validUntilCtx,
            contactId: contactIdCtx,
          });
          if (pre) {
            openContinue({
              ...pre,
              quoteMeta: { validUntil: validUntilCtx, contactId: contactIdCtx },
            }, (payload) => resumeSendQuoteFromContinuePayload(payload, {
              validUntil: validUntilCtx,
              contactId: contactIdCtx,
            }));
            return;
          }
        }
        try {
          await sendQuoteCore();
        } catch (error) {
          const quote = error?.__nr2jQuote;
          const req = requirementsFromError(error, {
            intent: NR2J_CONTINUE_INTENTS.QUOTE_SEND,
            org: orgCtx,
            contacts: contactsCtx,
            quoteId: quote?.id,
            versionId: quote?.currentVersion?.id,
            quoteMeta: { validUntil: validUntilCtx, contactId: contactIdCtx },
          });
          if (req) {
            openContinue(req, (payload) => resumeSendQuoteFromContinuePayload(payload, {
              validUntil: validUntilCtx,
              contactId: contactIdCtx,
            }));
            return;
          }
          if (isApprovalRequiredError(error)) {
            toast.error(approvalErrorMessage(error, 'Envoi impossible'));
            if (quote?.id) navigate(`/nr2j/devis/${quote.id}`, { replace: true });
          } else {
            toast.error(error?.message || 'Envoi impossible');
            if (quote?.id) navigate(`/nr2j/devis/${quote.id}`, { replace: true });
          }
        }
      } catch (error) {
        if (error?.message !== 'ORG_REQUIRED') {
          toast.error(error?.message || 'Envoi impossible');
        }
      } finally {
        setBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    const { skipped } = await withActionLock(run);
    if (skipped) return;
  }

  async function submitForApproval() {
    if (!validUntil) {
      applyGateMissing(['validUntil']);
      toast.error('Indiquez une date de validité');
      return;
    }
    setBusy(true);
    try {
      const quote = await persistQuote();
      try {
        const res = await apiService.submitNr2jQuote(quote.id);
        notifyNr2jDataChanged({ entityType: 'QUOTE', action: 'SUBMITTED' });
        const approver = res?.approval?.assignedApprover;
        toast.success(
          approver
            ? `Devis soumis pour validation à ${approvalUserName(approver)}`
            : 'Devis soumis pour validation',
        );
      } catch (error) {
        toast.error(approvalErrorMessage(error, 'Soumission pour validation impossible'));
      }
      navigate(`/nr2j/devis/${quote.id}`, { replace: true });
    } catch (error) {
      if (error?.message !== 'ORG_REQUIRED') {
        toast.error(error?.message || 'Enregistrement impossible');
      }
    } finally {
      setBusy(false);
    }
  }

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  const orgMissing = persistentMissing.filter((k) => NR2J_QUOTE_ORG_GATE_KEYS.has(k));
  const editorMissing = persistentMissing.filter((k) => NR2J_QUOTE_EDITOR_GATE_KEYS.has(k));

  return (
    <div className="space-y-6" data-testid="nr2j-quote-editor">
      <div className="sticky top-0 z-20 -mx-1 space-y-2 border-b border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link to="/nr2j/devis" className="inline-flex items-center gap-1 text-sm text-indigo-700">
              <ArrowLeft className="h-4 w-4" /> Retour aux devis
            </Link>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">Nouveau devis</h1>
            <p className="text-sm text-gray-600">
              Brouillon local — aucun numéro alloué tant que vous n’enregistrez ou n’envoyez pas.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="nr2j-quote-sticky-actions">
            <button
              type="button"
              disabled={busy || loading}
              onClick={saveDraft}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              Enregistrer
            </button>
            <button
              type="button"
              disabled={busy || loading}
              onClick={previewPdf}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              <Download className="mr-1 inline h-4 w-4" /> Aperçu PDF
            </button>
            {!accessReady ? (
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white opacity-50"
                data-testid="nr2j-quote-action-loading"
              >
                Chargement…
              </button>
            ) : needsApproval ? (
              <button
                type="button"
                disabled={busy || loading}
                onClick={submitForApproval}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                data-testid="nr2j-quote-submit-approval"
              >
                <ShieldCheck className="h-4 w-4" /> Soumettre pour validation
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || loading}
                onClick={sendQuote}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                data-testid="nr2j-quote-send-direct"
              >
                <Send className="h-4 w-4" /> Envoyer le devis
              </button>
            )}
          </div>
        </div>
      </div>

      {orgMissing.length > 0 ? (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          data-testid="nr2j-quote-org-missing"
        >
          <p className="font-semibold">Informations société à compléter</p>
          <ul className="mt-1 list-inside list-disc">
            {orgMissing.map((key) => (
              <li key={key}>{NR2J_QUOTE_SEND_GATE_LABELS[key] || key}</li>
            ))}
          </ul>
          {organizationId ? (
            <Link
              to={buildOrgFicheUrlFromQuoteGate({
                organizationId,
                quoteId: null,
                missingKeys: orgMissing,
              }) || `/nr2j/clients/${organizationId}`}
              className="mt-2 inline-block text-sm font-medium text-indigo-700 hover:underline"
            >
              Compléter la fiche société
            </Link>
          ) : null}
        </div>
      ) : null}

      {editorMissing.length > 0 ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {editorMissing.map((key) => (
            <p key={key}>{NR2J_QUOTE_SEND_GATE_LABELS[key] || key}</p>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="grid gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Société</h2>
              <div className="mt-3 block text-sm">
                <span className="text-gray-500">Société *</span>
                <div className="mt-1">
                  <Nr2jOrgCombobox
                    value={organizationId}
                    selectedOrg={org}
                    disabled={busy}
                    onChange={(next) => selectOrganization(next)}
                    onCreateCompany={(prefill) => {
                      setCreatePrefill(prefill || '');
                      setCreateOpen(true);
                    }}
                  />
                </div>
              </div>
              <label className="mt-3 block text-sm">
                <span className={
                  fieldErrors.contactId
                    ? 'font-medium text-rose-700'
                    : contactId
                      ? 'font-medium text-emerald-700'
                      : 'text-gray-500'
                }
                >
                  Contact destinataire
                </span>
                <select
                  className={`mt-1 w-full rounded-lg border px-3 py-2 ${
                    fieldErrors.contactId
                      ? 'border-rose-500 bg-rose-50'
                      : contactId
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-300'
                  }`}
                  value={contactId}
                  onChange={(e) => {
                    setContactId(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, contactId: false }));
                    if (e.target.value) {
                      setPersistentMissing((prev) => prev.filter((k) => k !== 'recipientEmail'));
                    }
                  }}
                >
                  <option value="">— (email facturation / principal)</option>
                  {(org?.contacts || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {contactDisplayName(c) || c.id}
                      {c.email ? ` (${c.email})` : ''}
                    </option>
                  ))}
                </select>
                {fieldErrors.contactId ? (
                  <span className="mt-1 block text-xs text-rose-700">Email destinataire requis pour l’envoi</span>
                ) : null}
              </label>
              <label className="mt-3 block text-sm">
                <span className={
                  fieldErrors.validUntil
                    ? 'font-medium text-rose-700'
                    : validUntil
                      ? 'font-medium text-emerald-700'
                      : 'text-gray-500'
                }
                >
                  Validité jusqu’au *
                </span>
                <input
                  type="date"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 ${
                    fieldErrors.validUntil
                      ? 'border-rose-500 bg-rose-50'
                      : validUntil
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-300'
                  }`}
                  value={validUntil}
                  onChange={(e) => {
                    setValidUntil(e.target.value);
                    if (e.target.value) {
                      setFieldErrors((prev) => ({ ...prev, validUntil: false }));
                      setPersistentMissing((prev) => prev.filter((k) => k !== 'validUntil'));
                    }
                  }}
                  data-testid="nr2j-quote-valid-until"
                />
                {fieldErrors.validUntil ? (
                  <span className="mt-1 block text-xs text-rose-700">Date de validité obligatoire avant envoi</span>
                ) : validUntil ? (
                  <span className="mt-1 block text-xs font-medium text-emerald-700">Champ valide</span>
                ) : (
                  <span className="mt-1 block text-xs text-gray-400">Prérempli à J+30 — modifiable</span>
                )}
              </label>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune ligne.</p>
                ) : (
                  lines.map((line, idx) => {
                    const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                    return (
                      <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm sm:col-span-2">
                            <span className="text-gray-500">Désignation *</span>
                            <input
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.name}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, name: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Qté</span>
                            <input
                              type="number"
                              min="1"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.quantity}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, quantity: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Prix unitaire HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.priceEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, priceEuros: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">TVA</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.vatRateBps}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                              )))}
                            >
                              {NR2J_VAT_OPTIONS.map((v) => (
                                <option key={v.bps} value={v.bps}>{v.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Facturation</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.billingType}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, billingType: e.target.value } : l
                              )))}
                            >
                              {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            HT {formatCentsAsEuros(lineTotals.lineHtCents)} € · TTC{' '}
                            {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                          </span>
                          <button
                            type="button"
                            className="text-rose-700 hover:underline"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {lines.length > 0 ? (
              <section
                className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-5 shadow-sm"
                data-testid="nr2j-quote-discounts-section"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-900">
                  Remises / avantages commerciaux
                </h2>
                <p className="mt-1 text-xs text-indigo-800/80">
                  La portée de chaque remise suit la fréquence de facturation de la ligne (ponctuel, mensuel ou annuel).
                </p>
                <div className="mt-4 space-y-4">
                  {lines.map((line, idx) => {
                    const payload = lineToPayload(line, 0);
                    const totals = computeQuoteLineTotals(payload);
                    const suffix = nr2jBillingPeriodSuffix(line.billingType);
                    const scope = nr2jDiscountScopeLabel(line.billingType);
                    const unitInitial = formatCentsAsEuros(payload.unitPriceHtCents);
                    const unitAfter = formatCentsAsEuros(
                      Math.round(totals.lineHtCents / Math.max(1, Number(line.quantity) || 1)),
                    );
                    return (
                      <div
                        key={line.key}
                        className="rounded-lg border border-indigo-100 bg-white p-3"
                        data-testid="nr2j-quote-line-discount"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          {line.name?.trim() || `Ligne ${idx + 1}`}
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm">
                            <span className="text-gray-500">Type de remise</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.discountMode}
                              onChange={(e) => {
                                const mode = e.target.value;
                                setLines((prev) => prev.map((l, i) => (
                                  i === idx
                                    ? {
                                      ...l,
                                      discountMode: mode,
                                      discountPercent: mode === 'percent' ? l.discountPercent : '',
                                      discountEuros: mode === 'fixed' ? l.discountEuros : '',
                                    }
                                    : l
                                )));
                              }}
                            >
                              <option value="none">Aucune</option>
                              <option value="percent">Pourcentage</option>
                              <option value="fixed">Montant fixe HT</option>
                            </select>
                          </label>
                          {line.discountMode === 'percent' ? (
                            <label className="block text-sm">
                              <span className="text-gray-500">Remise (%)</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                className="mt-1 w-full rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2"
                                value={line.discountPercent}
                                onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                  i === idx
                                    ? {
                                      ...l,
                                      discountMode: e.target.value === '' || Number(e.target.value) === 0
                                        ? 'none'
                                        : 'percent',
                                      discountPercent: e.target.value,
                                      discountEuros: '',
                                    }
                                    : l
                                )))}
                              />
                            </label>
                          ) : null}
                          {line.discountMode === 'fixed' ? (
                            <label className="block text-sm" data-testid="nr2j-quote-line-discount-fixed">
                              <span className="text-gray-500">Remise montant fixe HT (€)</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="mt-1 w-full rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2"
                                value={line.discountEuros}
                                onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                  i === idx
                                    ? {
                                      ...l,
                                      discountMode: e.target.value === '' || Number(e.target.value) === 0
                                        ? 'none'
                                        : 'fixed',
                                      discountEuros: e.target.value,
                                      discountPercent: '',
                                    }
                                    : l
                                )))}
                              />
                            </label>
                          ) : null}
                        </div>
                        <dl className="mt-3 grid gap-1 text-xs text-gray-600 sm:grid-cols-2">
                          <div>
                            <dt className="text-gray-500">Prix initial</dt>
                            <dd className="font-medium text-gray-900">
                              {unitInitial} € HT{suffix}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Portée</dt>
                            <dd className="font-medium text-gray-900">{scope}</dd>
                          </div>
                          {totals.discountHtCents > 0 ? (
                            <>
                              <div>
                                <dt className="text-gray-500">Remise commerciale</dt>
                                <dd className="font-medium text-indigo-800">
                                  {line.discountMode === 'percent'
                                    ? `${line.discountPercent || 0} %`
                                    : `${formatCentsAsEuros(totals.discountHtCents)} € HT`}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Prix après remise</dt>
                                <dd className="font-semibold text-emerald-800">
                                  {unitAfter} € HT{suffix}
                                </dd>
                              </div>
                            </>
                          ) : (
                            <div className="sm:col-span-2">
                              <dt className="text-gray-500">Prix après remise</dt>
                              <dd className="font-medium text-gray-700">
                                {unitInitial} € HT{suffix} (aucune remise)
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <label className="block text-sm">
                <span className="text-gray-500">Note client</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note interne</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                />
              </label>
            </section>
          </div>

          <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
            <Nr2jQuoteLivePreviewPanel lines={lines} lineToPayload={lineToPayload} />
          </aside>
        </div>
      )}

      <Nr2jContinueModal
        open={Boolean(continueReq)}
        requirements={continueReq?.requirements}
        onClose={closeContinue}
        onSaved={async (payload) => {
          if (payload?.org) {
            setOrg((prev) => ({
              ...(prev || {}),
              ...payload.org,
              contacts: payload.contacts || prev?.contacts,
            }));
          }
          if (payload?.validUntil) setValidUntil(payload.validUntil.slice(0, 10));
          if (payload?.quoteContactId) setContactId(payload.quoteContactId);
          const retry = continueReq?.retry;
          closeContinue();
          if (retry) await runContinueRetry(retry, payload);
        }}
      />

      {sendGateOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Fermer"
            onClick={() => setSendGateOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Envoi impossible</h3>
            <ul className="mt-3 space-y-1 text-sm text-rose-700">
              {persistentMissing.map((key) => (
                <li key={key}>✗ {NR2J_QUOTE_SEND_GATE_LABELS[key] || key}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setSendGateOpen(false)}
              className="mt-4 w-full rounded-lg border px-3 py-2 text-sm"
            >
              Fermer
            </button>
          </div>
        </div>
      ) : null}

      <AddProspectModal
        open={createOpen}
        busy={createBusy}
        title="Créer une nouvelle société"
        initialBusinessName={createPrefill}
        submitLabel="Créer la société"
        onClose={() => {
          setCreateOpen(false);
          setCreatePrefill('');
        }}
        onSubmit={async (payload) => {
          setCreateBusy(true);
          try {
            const { organization } = await createNr2jProspectFromModal(apiService, payload);
            if (!organization?.id) {
              throw new Error('Société créée mais identifiant manquant — ouvrez la prospection.');
            }
            const detail = await apiService.getNr2jOrganization(organization.id);
            selectOrganization(detail?.organization || detail);
            setCreateOpen(false);
            setCreatePrefill('');
            toast.success('Société créée — vous pouvez poursuivre le devis');
            // Pas de remount : brouillon local (lignes, notes, validUntil) préservé via modal.
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
